/**
 * useAdvisorClients Hook
 * Feature 020: Resource Sharing with Care Seeker Clients
 * Feature 054: Now uses unified client-relationships API
 *
 * Returns the FA's client list in the shape expected by resource-hub
 * components (flat array with `name` field).
 */

import { useQuery } from '@tanstack/react-query';
import {
  clientRelationshipsApi,
  clientKeys,
  type Client,
} from '@/features/client-relationships';
import type { PaginatedClients } from '@/features/client-relationships';

type ResourceClient = {
  id: string;
  name: string;
  email: string;
  profilePhoto?: string;
};

type UseAdvisorClientsOptions = {
  enabled?: boolean;
};

const toResourceClient = (client: Client): ResourceClient => ({
  id: client.id,
  name: [client.firstName, client.lastName].filter(Boolean).join(' '),
  email: client.email,
  profilePhoto: client.profilePhoto,
});

export function useAdvisorClients(options?: UseAdvisorClientsOptions) {
  return useQuery({
    queryKey: clientKeys.list(),
    queryFn: () => clientRelationshipsApi.getClients({ limit: 50 }), // Maximum allowed by API
    select: (data: PaginatedClients): ResourceClient[] =>
      data.data.map(toResourceClient),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
